<template>
  <div class="time-usage-tab" v-loading="loading">
    <!-- 时间统计卡片 -->
    <el-row :gutter="20" class="summary-row">
      <el-col :span="8">
        <div class="summary-card">
          <div class="summary-icon" style="background: #ecf5ff; color: #409eff">
            <i class="el-icon-time"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ timeStats.totalHours || 0 }}</div>
            <div class="summary-label">累计学习时长(小时)</div>
          </div>
        </div>
      </el-col>
      <el-col :span="8">
        <div class="summary-card">
          <div class="summary-icon" style="background: #f0f9ff; color: #67c23a">
            <i class="el-icon-date"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ timeStats.weekHours || 0 }}</div>
            <div class="summary-label">本周学习时长(小时)</div>
          </div>
        </div>
      </el-col>
      <el-col :span="8">
        <div class="summary-card">
          <div class="summary-icon" style="background: #fdf6ec; color: #e6a23c">
            <i class="el-icon-calendar"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ timeStats.monthHours || 0 }}</div>
            <div class="summary-label">本月学习时长(小时)</div>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- 周学习时间柱状图 -->
    <el-card class="chart-card" shadow="never">
      <div slot="header" class="card-header">
        <span>周学习时间分布</span>
        <el-date-picker
          v-model="weekDate"
          type="week"
          size="small"
          format="yyyy 第 WW 周"
          placeholder="选择周"
          @change="handleWeekChange"
        />
      </div>
      <div ref="weekChart" class="chart-container"></div>
    </el-card>

    <!-- 月学习时间趋势图 -->
    <el-card class="chart-card" shadow="never">
      <div slot="header" class="card-header">
        <span>月学习时间趋势</span>
        <el-date-picker
          v-model="monthDate"
          type="month"
          size="small"
          format="yyyy-MM"
          placeholder="选择月份"
          @change="handleMonthChange"
        />
      </div>
      <div ref="monthChart" class="chart-container"></div>
    </el-card>

    <!-- 时间分布详情 -->
    <el-card class="detail-card" shadow="never">
      <div slot="header" class="card-header">
        <span>学习时段分布</span>
      </div>
      <div ref="periodChart" class="chart-container"></div>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { mapActions } from 'vuex';

export default {
  name: 'TimeUsageTab',
  props: {
    studentId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      weekDate: null,
      monthDate: null,
      timeStats: {},
      weekData: [],
      monthData: [],
      periodData: [],
      weekChart: null,
      monthChart: null,
      periodChart: null,
    };
  },
  created() {
    // 设置默认日期
    this.weekDate = new Date();
    this.monthDate = new Date();
    this.loadTimeData();
  },
  mounted() {
    this.initCharts();
  },
  beforeDestroy() {
    if (this.weekChart) this.weekChart.dispose();
    if (this.monthChart) this.monthChart.dispose();
    if (this.periodChart) this.periodChart.dispose();
  },
  methods: {
    ...mapActions('student', ['fetchAllTimeData']),

    /**
     * 加载时间数据 (优化版:一次接口调用获取所有数据)
     */
    async loadTimeData() {
      this.loading = true;
      try {
        // 一次接口调用获取所有时间相关数据
        const result = await this.fetchAllTimeData(this.studentId);
        console.log('时间数据响应:', result);
        this.timeStats = result.timeStats;
        this.weekData = result.weekData;
        this.monthData = result.monthData;
        this.periodData = result.periodData;
        
        this.updateWeekChart();
        this.updateMonthChart();
        this.updatePeriodChart();
      } catch (error) {
        console.error('加载时间数据失败:', error);
        this.$message.error('加载时间数据失败');
      } finally {
        this.loading = false;
      }
    },

    /**
     * 周改变 (重新加载所有数据)
     */
    async handleWeekChange() {
      try {
        const result = await this.fetchAllTimeData(this.studentId);
        this.weekData = result.weekData;
        this.updateWeekChart();
      } catch (error) {
        console.error('加载周数据失败:', error);
      }
    },

    /**
     * 月改变 (重新加载所有数据)
     */
    async handleMonthChange() {
      try {
        const result = await this.fetchAllTimeData(this.studentId);
        this.monthData = result.monthData;
        this.updateMonthChart();
      } catch (error) {
        console.error('加载月数据失败:', error);
      }
    },

    /**
     * 初始化图表
     */
    initCharts() {
      this.weekChart = echarts.init(this.$refs.weekChart);
      this.monthChart = echarts.init(this.$refs.monthChart);
      this.periodChart = echarts.init(this.$refs.periodChart);
      
      this.updateWeekChart();
      this.updateMonthChart();
      this.updatePeriodChart();
    },

    /**
     * 更新周图表
     */
    updateWeekChart() {
      if (!this.weekChart) return;

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: '{b}<br/>学习时长: {c} 分钟',
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
        },
        yAxis: {
          type: 'value',
          name: '时长(分钟)',
        },
        series: [
          {
            name: '学习时长',
            type: 'bar',
            data: this.weekData,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#409eff' },
                { offset: 1, color: '#66b1ff' },
              ]),
              borderRadius: [5, 5, 0, 0],
            },
            label: {
              show: true,
              position: 'top',
              formatter: '{c} 分',
            },
          },
        ],
      };

      this.weekChart.setOption(option);
    },

    /**
     * 更新月图表
     */
    updateMonthChart() {
      if (!this.monthChart) return;

      const dates = this.monthData.map((item) => item.date);
      const durations = this.monthData.map((item) => item.duration);

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          formatter: '{b}<br/>学习时长: {c} 分钟',
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: false,
        },
        yAxis: {
          type: 'value',
          name: '时长(分钟)',
        },
        series: [
          {
            name: '学习时长',
            type: 'line',
            data: durations,
            smooth: true,
            itemStyle: { color: '#67c23a' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(103, 194, 58, 0.5)' },
                { offset: 1, color: 'rgba(103, 194, 58, 0.1)' },
              ]),
            },
          },
        ],
      };

      this.monthChart.setOption(option);
    },

    /**
     * 更新时段图表
     */
    updatePeriodChart() {
      if (!this.periodChart) return;

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} 分钟 ({d}%)',
        },
        legend: {
          orient: 'vertical',
          right: 20,
          top: 'center',
        },
        series: [
          {
            name: '学习时段',
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['40%', '50%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2,
            },
            label: {
              show: true,
              formatter: '{b}\n{d}%',
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 16,
                fontWeight: 'bold',
              },
            },
            data: [
              { value: this.periodData.morning || 0, name: '早上(6-12点)', itemStyle: { color: '#409eff' } },
              { value: this.periodData.afternoon || 0, name: '下午(12-18点)', itemStyle: { color: '#67c23a' } },
              { value: this.periodData.evening || 0, name: '晚上(18-24点)', itemStyle: { color: '#e6a23c' } },
              { value: this.periodData.night || 0, name: '深夜(0-6点)', itemStyle: { color: '#909399' } },
            ],
          },
        ],
      };

      this.periodChart.setOption(option);
    },
  },
};
</script>

<style scoped>
.time-usage-tab {
  padding: 10px 0;
}

.summary-row {
  margin-bottom: 20px;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.summary-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 24px;
}

.summary-content {
  flex: 1;
}

.summary-value {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 5px;
}

.summary-label {
  font-size: 13px;
  color: #909399;
}

.chart-card,
.detail-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  color: #303133;
}

.chart-container {
  height: 400px;
  width: 100%;
}
</style>
